import type {
  Layout,
  RowNode,
  TabNode,
  TabsetNode,
  Tree,
} from "@/layout/layout-utils";
import { v4 as uuidv4 } from "uuid";

export type MobileLayoutView = "markets" | "trade" | "account";

// Two-column layout: Left column (chart, order-book, positions) is resizable,
// Right column (order-form, account) maintains fixed dimensions
export const primaryLayout: Layout = {
  version: "1",
  data: {
    id: uuidv4(),
    name: "Primary",
    tree: {
      type: "row",
      id: uuidv4(),
      children: [
        // Main horizontal row containing left and right columns
        {
          type: "row",
          id: uuidv4(),
          weight: 100,
          children: [
            // Left Column: Chart + OrderBook (top) | Positions (bottom)
            // This column is resizable between chart area and positions
            {
              type: "row",
              id: uuidv4(),
              weight: 75,
              children: [
                // Top area: Chart + Order Book side by side
                {
                  type: "row",
                  id: uuidv4(),
                  weight: 70,
                  children: [
                    {
                      type: "tabset",
                      id: uuidv4(),
                      weight: 78,
                      children: [
                        {
                          type: "tab",
                          id: uuidv4(),
                          panelTypeId: "market-chart",
                        },
                      ],
                    },
                    {
                      type: "tabset",
                      id: uuidv4(),
                      weight: 22,
                      children: [
                        {
                          type: "tab",
                          id: uuidv4(),
                          panelTypeId: "order-book",
                        },
                      ],
                    },
                  ],
                },
                // Bottom area: Positions panel (full width of left column)
                {
                  type: "tabset",
                  id: uuidv4(),
                  weight: 30,
                  children: [
                    {
                      type: "tab",
                      id: uuidv4(),
                      panelTypeId: "positions",
                    },
                  ],
                },
              ],
            },
            // Right Column: Order Form (top) | Account (bottom)
            // This column has fixed width and doesn't resize with positions
            // Order form gets most space (weight 100), account is minimal (weight 1) to stay at bottom
            {
              type: "row",
              id: uuidv4(),
              weight: 25,
              children: [
                {
                  type: "tabset",
                  id: uuidv4(),
                  weight: 100,
                  children: [
                    {
                      type: "tab",
                      id: uuidv4(),
                      panelTypeId: "order-form",
                    },
                  ],
                },
                {
                  type: "tabset",
                  id: uuidv4(),
                  weight: 1,
                  children: [
                    {
                      type: "tab",
                      id: uuidv4(),
                      panelTypeId: "account",
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  },
};

// Panel visibility type
export type PanelVisibilityConfig = {
  orderBookVisible: boolean;
  accountVisible: boolean;
};

// Generate a layout tree based on panel visibility settings
// Uses the same two-column structure as primaryLayout
export const createLayoutWithVisibility = (visibility: PanelVisibilityConfig): Tree => {
  // Build chart + order book area for top of left column
  const chartOrderBookChildren: (TabsetNode | RowNode)[] = [
    {
      type: "tabset",
      id: uuidv4(),
      weight: visibility.orderBookVisible ? 78 : 100,
      children: [
        {
          type: "tab",
          id: uuidv4(),
          panelTypeId: "market-chart",
        },
      ],
    },
  ];

  if (visibility.orderBookVisible) {
    chartOrderBookChildren.push({
      type: "tabset",
      id: uuidv4(),
      weight: 22,
      children: [
        {
          type: "tab",
          id: uuidv4(),
          panelTypeId: "order-book",
        },
      ],
    });
  }

  // Build right column children (order form + optionally account)
  // Order form gets most space (weight 100), account is minimal (weight 1) to stay at bottom
  const rightColumnChildren: (TabsetNode | RowNode)[] = [
    {
      type: "tabset",
      id: uuidv4(),
      weight: visibility.accountVisible ? 100 : 100,
      children: [
        {
          type: "tab",
          id: uuidv4(),
          panelTypeId: "order-form",
        },
      ],
    },
  ];

  if (visibility.accountVisible) {
    rightColumnChildren.push({
      type: "tabset",
      id: uuidv4(),
      weight: 1,
      children: [
        {
          type: "tab",
          id: uuidv4(),
          panelTypeId: "account",
        },
      ],
    });
  }

  return {
    type: "row",
    id: uuidv4(),
    children: [
      // Main horizontal row containing left and right columns
      {
        type: "row",
        id: uuidv4(),
        weight: 100,
        children: [
          // Left Column: Chart + OrderBook (top) | Positions (bottom)
          {
            type: "row",
            id: uuidv4(),
            weight: 75,
            children: [
              // Top area: Chart + Order Book side by side
              {
                type: "row",
                id: uuidv4(),
                weight: 70,
                children: chartOrderBookChildren,
              },
              // Bottom area: Positions panel
              {
                type: "tabset",
                id: uuidv4(),
                weight: 30,
                children: [
                  {
                    type: "tab",
                    id: uuidv4(),
                    panelTypeId: "positions",
                  },
                ],
              },
            ],
          },
          // Right Column: Order Form + Account
          {
            type: "row",
            id: uuidv4(),
            weight: 25,
            children: rightColumnChildren,
          },
        ],
      },
    ],
  };
};

const cloneTabNode = (node: TabNode): TabNode => ({
  type: "tab",
  id: node.id,
  panelTypeId: node.panelTypeId,
});

const cloneTabsetNode = (node: TabsetNode): TabsetNode => ({
  type: "tabset",
  id: node.id,
  weight: node.weight,
  selected: node.selected,
  children: node.children.map((child) => cloneTabNode(child)),
});

const cloneTree = (node: RowNode): RowNode => ({
  type: "row",
  id: node.id,
  weight: node.weight,
  children: node.children.map((child) =>
    child.type === "tabset" ? cloneTabsetNode(child) : cloneTree(child)
  ),
});

const mobileLayoutTrees: Record<MobileLayoutView, Tree> = {
  markets: {
    type: "row",
    id: "mobile-markets-root",
    children: [
      {
        type: "row",
        id: "mobile-markets-chart-row",
        weight: 72,
        children: [
          {
            type: "tabset",
            id: "mobile-markets-chart-tabset",
            weight: 100,
            children: [
              {
                type: "tab",
                id: "mobile-markets-chart-tab",
                panelTypeId: "market-chart",
              },
            ],
          },
        ],
      },
      {
        type: "row",
        id: "mobile-markets-positions-row",
        weight: 28,
        children: [
          {
            type: "tabset",
            id: "mobile-markets-positions-tabset",
            weight: 100,
            children: [
              {
                type: "tab",
                id: "mobile-markets-positions-tab",
                panelTypeId: "positions",
              },
            ],
          },
        ],
      },
    ],
  },
  trade: {
    type: "row",
    id: "mobile-trade-root",
    children: [
      {
        type: "row",
        id: "mobile-trade-main-row",
        weight: 72,
        children: [
          {
            type: "tabset",
            id: "mobile-trade-order-book-tabset",
            weight: 40,
            children: [
              {
                type: "tab",
                id: "mobile-trade-order-book-tab",
                panelTypeId: "order-book",
              },
            ],
          },
          {
            type: "tabset",
            id: "mobile-trade-order-form-tabset",
            weight: 60,
            children: [
              {
                type: "tab",
                id: "mobile-trade-order-form-tab",
                panelTypeId: "order-form",
              },
            ],
          },
        ],
      },
      {
        type: "row",
        id: "mobile-trade-positions-row",
        weight: 28,
        children: [
          {
            type: "tabset",
            id: "mobile-trade-positions-tabset",
            weight: 100,
            children: [
              {
                type: "tab",
                id: "mobile-trade-positions-tab",
                panelTypeId: "positions",
              },
            ],
          },
        ],
      },
    ],
  },
  account: {
    type: "row",
    id: "mobile-account-root",
    children: [
      {
        type: "tabset",
        id: "mobile-account-balances-tabset",
        weight: 100,
        children: [
          {
            type: "tab",
            id: "mobile-account-balances-tab",
            panelTypeId: "account",
          },
        ],
      },
    ],
  },
};

export const createMobileLayout = (view: MobileLayoutView): Layout => ({
  version: "1",
  data: {
    id: `mobile-${view}`,
    name: `Mobile ${view}`,
    tree: cloneTree(mobileLayoutTrees[view]),
  },
});
